import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isSameDay,
  parseISO
} from "date-fns";
import {
  getUnavailableTimeSlotsApi,
  addUnavailableTimeSlotApi,
  removeUnavailableTimeSlotApi,
  removeAllUnavailableTimeSlotsForDateApi
} from "../../Apis/unavailableTimeSlots/unavailableTimeSlots";
import { useToast } from "../../Hooks/useToast";

const CELL_SIZE = 50;

// دالة محسنة لتنسيق التاريخ بدون مشاكل فرق التوقيت
const formatDateSafe = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const UnavailableTimeSlots = () => {
  // State management
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dataSource, setDataSource] = useState('');
  
  const { toastSuccess, toastError } = useToast();

  // تحميل الأوقات غير المتاحة عند بدء التشغيل
  useEffect(() => {
    loadUnavailableTimeSlots();
  }, []);

  const loadUnavailableTimeSlots = async () => {
    try {
      setIsLoading(true);
      
      const response = await getUnavailableTimeSlotsApi();
      
      if (response.success) {
        const slots = response.unavailableTimeSlots || [];
        setUnavailableTimeSlots(slots);
        setDataSource(response.source || 'unknown');
        
       
        return slots; // إرجاع البيانات للاستخدام في التحقق
        
      } 
    }  finally {
      setIsLoading(false);
    }
  };

  // فتح حوار إضافة وقت جديد
  const handleDateClick = (date) => {
    const dateFormatted = formatDateSafe(date);
    setSelectedDate(dateFormatted);
    setStartTime(null);
    setEndTime(null);
    setIsDialogOpen(true);
  };

  // إضافة فترة زمنية غير متاحة - الإصلاح النهائي الذي يعمل
  const handleAddTimeSlot = async () => {
    if (!selectedDate || !startTime || !endTime) {
      toastError("Bitte wählen Sie Start- und Endzeit aus");
      return;
    }

    try {
      setIsUpdating(true);
      
      const startTimeFormatted = format(startTime, "HH:mm");
      const endTimeFormatted = format(endTime, "HH:mm");
      
    
      
      // الخطوة 1: إرسال الطلب للباك إند
      const response = await addUnavailableTimeSlotApi(
        selectedDate,
        startTimeFormatted,
        endTimeFormatted
      );
      
      
      if (response.success) {
        
        // الخطوة 2: إعادة تحميل البيانات من الخادم مباشرة
        const freshSlots = await loadUnavailableTimeSlots();
        
        // الخطوة 3: التحقق من وجود الوقت المُضاف في البيانات الجديدة
        const targetEntry = freshSlots.find(entry => entry.date === selectedDate);
        const wasAdded = targetEntry && targetEntry.timeSlots && 
          targetEntry.timeSlots.some(slot => 
            slot.startTime === startTimeFormatted && slot.endTime === endTimeFormatted
          );
        
   
        
        if (wasAdded) {
          // النجاح - إغلاق الحوار وإظهار رسالة النجاح
          setIsDialogOpen(false);
          setStartTime(null);
          setEndTime(null);
          
          toastSuccess(`Nicht verfügbare Zeit erfolgreich hinzugefügt: ${startTimeFormatted} - ${endTimeFormatted}`);
          
          
        } else {
          // فشل في العثور على البيانات
       
          
          // رسالة خطأ مع معلومات مفيدة
          toastError("Zeit wurde hinzugefügt, aber nicht in der Anzeige gefunden. Die Seite wird aktualisiert...");
          
          // إعادة تحميل الصفحة كحل أخير
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        
      } 
    }  finally {
      setIsUpdating(false);
    }
  };

  // حذف فترة زمنية غير متاحة
  const handleRemoveTimeSlot = async (date, timeSlotId) => {
    try {
      setIsUpdating(true);
      
      
      const response = await removeUnavailableTimeSlotApi(date, timeSlotId);
      
      if (response.success) {
        // إعادة تحميل البيانات للتأكد من التحديث
        await loadUnavailableTimeSlots();
        toastSuccess("Nicht verfügbare Zeit erfolgreich entfernt");
        
        
      } else {
        toastError(response.error || "Fehler beim Entfernen der Zeit");
      }
    } catch (error) {
      toastError(error.response?.data?.error || "Fehler beim Entfernen der Zeit");
    } finally {
      setIsUpdating(false);
    }
  };

  // حذف جميع الأوقات لتاريخ معين
  const handleRemoveAllTimeSlotsForDate = async (date) => {
    try {
      setIsUpdating(true);
      
      
      const response = await removeAllUnavailableTimeSlotsForDateApi(date);
      
      if (response.success) {
        // إعادة تحميل البيانات للتأكد من التحديث
        await loadUnavailableTimeSlots();
        toastSuccess("Alle nicht verfügbaren Zeiten für dieses Datum entfernt");
        
        
      } else {
        toastError(response.error || "Fehler beim Entfernen der Zeiten");
      }
    } catch (error) {
      toastError(error.response?.data?.error || "Fehler beim Entfernen der Zeiten");
    } finally {
      setIsUpdating(false);
    }
  };

  // التنقل بين الشهور
  const nextMonth = () => setCurrentMonth(addDays(endOfMonth(currentMonth), 1));
  const prevMonth = () => setCurrentMonth(addDays(startOfMonth(currentMonth), -1));

  // رأس التقويم
  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <IconButton onClick={prevMonth} disabled={isUpdating}>
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" fontWeight={600}>
        {format(currentMonth, "MMMM yyyy")}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton 
          onClick={loadUnavailableTimeSlots} 
          disabled={isLoading || isUpdating}
          size="small"
          title="Daten neu laden"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={nextMonth} disabled={isUpdating}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  // التحقق من وجود أوقات غير متاحة لتاريخ معين
  const hasUnavailableTimeSlots = (date) => {
    const dateFormatted = formatDateSafe(date);
    const entry = unavailableTimeSlots.find(entry => entry.date === dateFormatted);
    const hasSlots = entry && entry.timeSlots && entry.timeSlots.length > 0;
    
    return hasSlots;
  };

  // رسم التقويم
  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(new Date(day));
      day = addDays(day, 1);
    }

    return (
      <Grid container spacing={1} justifyContent="flex-start">
        {days.map((dayItem) => {
          if (!isSameMonth(dayItem, monthStart)) return null;
          
          const formatted = format(dayItem, "d");
          const hasUnavailable = hasUnavailableTimeSlots(dayItem);
          const isToday = isSameDay(dayItem, new Date());

          return (
            <Grid item key={dayItem.toString()}>
              <Box 
                sx={{
                  width: CELL_SIZE,
                  height: CELL_SIZE,
                  borderRadius: "50%",
                  bgcolor: isToday ? "#caa96a" : hasUnavailable ? "#ffebee" : "#ffffff",
                  border: hasUnavailable ? "2px solid #f44336" : "1px solid #e0e0e0",
                  color: hasUnavailable ? "#f44336" : "black",
                  fontWeight: isToday ? "bold" : hasUnavailable ? "bold" : "normal",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isUpdating ? "wait" : "pointer",
                  opacity: isUpdating ? 0.6 : 1,
                  '&:hover': {
                    backgroundColor: isToday ? "#caa96a" : hasUnavailable ? "#ffcdd2" : "#f5f5f5"
                  }
                }} 
                onClick={() => !isUpdating && handleDateClick(dayItem)}
              >
                <Typography variant="body2">{formatted}</Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // عرض قائمة الأوقات غير المتاحة مع تصميم بوكسات صغيرة ومرتبة
  const renderUnavailableTimeSlotsList = () => {
   ;

    // فلترة الإدخالات التي تحتوي على أوقات فعلية
    const entriesWithTimeSlots = unavailableTimeSlots.filter(entry => 
      entry.timeSlots && entry.timeSlots.length > 0
    );


    if (entriesWithTimeSlots.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            Keine aktiven nicht verfügbaren Zeiten
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Klicken Sie auf einen Tag im Kalender, um Zeiten hinzuzufügen
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        {/* عرض البوكسات في شبكة مرتبة */}
        <Grid container spacing={2}>
          {entriesWithTimeSlots.map(dateEntry => (
            <Grid item xs={12} sm={6} md={4} key={dateEntry.date}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: '2px solid #f44336',
                  backgroundColor: '#fff5f5',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  {/* رأس البوكس مع التاريخ */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarTodayIcon sx={{ color: '#f44336', mr: 1, fontSize: '1.2rem' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} color="#f44336">
                        {format(parseISO(dateEntry.date), "dd.MM.yyyy")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {dateEntry.timeSlots.length} Zeitslot{dateEntry.timeSlots.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* الأوقات غير المتاحة */}
                  <Typography variant="caption" fontWeight={600} color="#d32f2f" sx={{ mb: 1, display: 'block' }}>
                    🚫 Nicht verfügbare Zeiten:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {dateEntry.timeSlots.map((timeSlot, index) => {
                      // إنشاء ID فريد إذا لم يكن موجوداً
                      const timeSlotId = timeSlot._id || `${dateEntry.date}-${index}-${timeSlot.startTime}-${timeSlot.endTime}`;
                      
                      return (
                        <Chip
                          key={timeSlotId}
                          icon={<AccessTimeIcon sx={{ fontSize: '0.8rem !important' }} />}
                          label={`${timeSlot.startTime} - ${timeSlot.endTime}`}
                          onDelete={() => handleRemoveTimeSlot(dateEntry.date, timeSlotId)}
                          disabled={isUpdating}
                          size="small"
                          sx={{ 
                            backgroundColor: '#f44336', 
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            height: '24px',
                            opacity: isUpdating ? 0.6 : 1,
                            '& .MuiChip-icon': {
                              color: 'white',
                              fontSize: '0.8rem'
                            },
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              fontSize: '0.9rem',
                              '&:hover': {
                                color: '#ffcdd2'
                              }
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </CardContent>
                
                <CardActions sx={{ pt: 0, pb: 2, px: 2, justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="#e65100" sx={{ fontSize: '0.7rem' }}>
                    ⚠️ Für Kunden nicht verfügbar
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveAllTimeSlotsForDate(dateEntry.date)}
                    disabled={isUpdating}
                    title="Alle Zeiten für dieses Datum löschen"
                    sx={{ 
                      backgroundColor: '#ffebee',
                      '&:hover': { backgroundColor: '#ffcdd2' },
                      width: 28,
                      height: 28
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* معلومات المصدر */}
      
        
        {isUpdating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Lade nicht verfügbare Zeiten...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" color="black" fontWeight={500} mb={2} gutterBottom>
        Nicht verfügbare Zeiten verwalten
      </Typography>
      
    
      
      {renderHeader()}
      {renderCalendar()}
      
      <Typography variant="body1" color="black" fontWeight={500} mt={4} gutterBottom>
        Aktuelle nicht verfügbare Zeiten:
      </Typography>
      {renderUnavailableTimeSlotsList()}

      {/* Dialog für das Hinzufügen neuer Zeiten */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Nicht verfügbare Zeit hinzufügen
          {selectedDate && (
            <Typography variant="body2" color="textSecondary">
              Datum: {format(parseISO(selectedDate), "dd.MM.yyyy")}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TimePicker
                  label="Startzeit"
                  value={startTime}
                  onChange={setStartTime}
                  ampm={false}
                  minutesStep={15}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !startTime && startTime !== null,
                      helperText: !startTime && startTime !== null ? "Startzeit ist erforderlich" : "",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label="Endzeit"
                  value={endTime}
                  onChange={setEndTime}
                  ampm={false}
                  minutesStep={15}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !endTime && endTime !== null,
                      helperText: !endTime && endTime !== null ? "Endzeit ist erforderlich" : "",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)} disabled={isUpdating}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleAddTimeSlot} 
            variant="contained" 
            disabled={isUpdating || !startTime || !endTime}
            startIcon={isUpdating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {isUpdating ? 'Wird hinzugefügt...' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnavailableTimeSlots;